import React, { useEffect, useState } from "react";
import Table from "../../applicationUi/components/Table";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchServices,
  fetchServiceById,
  exportServices,
} from "../../store/thunks/servicesThunk";
import EditServiceModal from "../services/EditServiceModal";
import { fetchRoles } from "../../store/thunks/rolesThunk";
import { logError, logInfo } from "../../utils/logger";

function ServicesPage({ clickedItem }) {
  const dispatch = useDispatch();
  const {
    services,
    pagination,
    status: serviceStatus,
    error: serviceError,
  } = useSelector((state) => state.services);

  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch services based on current tab and search
  const fetchServicesData = (page = 1) => {
    const active = activeTab === "active";
    logInfo("[Services Page] Fetching services with filters", { 
      active, 
      search: searchTerm, 
      page,
      limit: pagination?.pageSize || 10
    });

    dispatch(
      fetchServices({
        page,
        limit: pagination?.pageSize || 10,
        active,
        search: searchTerm,
      })
    );
  };

  // Handle clicked item from navigation
  useEffect(() => {
    if (clickedItem && clickedItem._id) {
      logInfo("[Services Page] Fetching specific service", { serviceId: clickedItem._id });
      dispatch(fetchServiceById(clickedItem._id))
        .unwrap()
        .then((service) => {
          logInfo("[Services Page] Specific service fetched successfully", {
            serviceId: service._id,
          });
          setSelectedService(service);
          setIsServiceModalOpen(true);
        })
        .catch((error) => {
          logError("[Services Page] Failed to fetch specific service", {
            error,
            serviceId: clickedItem._id,
          });
        });
    }
  }, [dispatch, clickedItem]);

  // Fetch initial data and roles
  useEffect(() => {
    fetchServicesData(1);
    dispatch(fetchRoles());
  }, [dispatch, activeTab, searchTerm]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== "") {
        fetchServicesData(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleRowClick = (serviceId) => {
    const selectedService = services.find(
      (service) => service._id === serviceId
    );
    setIsServiceModalOpen(true);
    setSelectedService(selectedService);
  };

  const handleAddService = () => {
    setIsServiceModalOpen(true);
    setSelectedService(null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm(""); // Clear search when switching tabs
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    fetchServicesData(page);
  };

  const handleModalSave = () => {
    setIsServiceModalOpen(false);
    setSelectedService(null);
    fetchServicesData(pagination?.currentPage || 1);
  };

  return (
    <div className="p-6">
      {/* Header with Search */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">Services</h1>
        
        {/* Search Bar */}
        <div className="max-w-md">
          <input
            type="text"
            placeholder="Search services by code, description..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange("active")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "active"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Active Services
          </button>
          <button
            onClick={() => handleTabChange("inactive")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "inactive"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Inactive Services
          </button>
        </nav>
      </div>

      {/* Loading/Error States */}
      {serviceStatus === "loading" && (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading services...</p>
        </div>
      )}
      
      {serviceStatus === "failed" && (
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading services: {typeof serviceError === "object" ? JSON.stringify(serviceError) : serviceError}
          </p>
        </div>
      )}

      {/* Services Table */}
      {serviceStatus === "succeeded" && (
        <>
          {services.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No {activeTab} services found{searchTerm ? ` matching "${searchTerm}"` : ""}.
              </p>
            </div>
          ) : (
            <Table
              columns={[
                { header: "Service Code", accessor: "serviceCode" },
                { header: "Description", accessor: "description" },
                { header: "Duration", accessor: "duration" },
                { header: "Price", accessor: "price" },
                { header: "Color", accessor: "color" },
              ]}
              data={services}
              onRowClick={handleRowClick}
              actions={[
                {
                  label: "Add Service",
                  onClick: handleAddService,
                },
                {
                  label: "Download XLSX",
                  onClick: () => dispatch(exportServices()),
                },
              ]}
              title={`${activeTab === "active" ? "Active" : "Inactive"} Services`}
              description={`A list of all ${activeTab} services${searchTerm ? ` matching "${searchTerm}"` : ""}.`}
              showPagination={pagination?.totalPages > 1}
              currentPage={pagination?.currentPage}
              totalPages={pagination?.totalPages}
              totalResults={pagination?.totalServices}
              pageSize={pagination?.pageSize}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Service Modal */}
      {isServiceModalOpen && (
        <EditServiceModal
          service={selectedService}
          onClose={() => {
            setIsServiceModalOpen(false);
            setSelectedService(null);
          }}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

export default ServicesPage;
